/**
 * Adapter for @capawesome/capacitor-app-shortcuts plugin.
 *
 * This adapter uses dynamic imports to ensure the plugin code is only loaded
 * on native versions that support it. The __SUPPORTS_APP_SHORTCUTS__ flag is
 * injected at build time based on the target native baseline.
 *
 * @see nativeCapabilities.config.ts
 * @see webpack.config.js
 */

import type {
  ClickEvent,
  Shortcut as CapawesomeShortcut,
} from "@capawesome/capacitor-app-shortcuts";
import { isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor/platformUtils";

// Define our own types to avoid barrel file issues
export interface Shortcut {
  id: string;
  title: string;
  description?: string;
  iosIcon?: number | string;
  androidIcon?: number | string;
}

export type ShortcutClickEvent = ClickEvent;

export interface ShortcutClickListener {
  remove: () => Promise<void>;
}

/**
 * Returns true if app shortcuts are supported on the current platform/version.
 */
export function isAppShortcutsAvailable(): boolean {
  // __SUPPORTS_APP_SHORTCUTS__ is injected at build time by webpack DefinePlugin
  return Boolean(__SUPPORTS_APP_SHORTCUTS__) && isCapacitorPlatform();
}

/**
 * Sets the app shortcuts. Clears existing shortcuts first.
 * No-op on unsupported platforms/versions.
 */
export async function setShortcuts(shortcuts: Shortcut[]): Promise<void> {
  if (!isAppShortcutsAvailable()) {
    return;
  }

  // Use dynamic import to ensure code is only loaded when available
  const { AppShortcuts } = await import("@capawesome/capacitor-app-shortcuts");
  await AppShortcuts.set({ shortcuts: shortcuts as CapawesomeShortcut[] });
}

/**
 * Clears all app shortcuts.
 * No-op on unsupported platforms/versions.
 */
export async function clearShortcuts(): Promise<void> {
  if (!isAppShortcutsAvailable()) {
    return;
  }

  // Use dynamic import to ensure code is only loaded when available
  const { AppShortcuts } = await import("@capawesome/capacitor-app-shortcuts");
  await AppShortcuts.clear();
}

/**
 * Adds a listener for shortcut click events.
 * Returns undefined on unsupported platforms/versions.
 */
export async function addShortcutClickListener(
  callback: (event: ShortcutClickEvent) => void
): Promise<ShortcutClickListener | undefined> {
  if (!isAppShortcutsAvailable()) {
    return undefined;
  }

  // Use dynamic import to ensure code is only loaded when available
  const { AppShortcuts } = await import("@capawesome/capacitor-app-shortcuts");
  return await AppShortcuts.addListener("click", callback);
}
